/*
  Metcos — keeps the delivery rule truthful as the page changes.

  Two jobs:

  1. Cart progress bar. Liquid renders the bar server-side so there is no flash
     of wrong text, but Dawn swaps cart sections over fetch when a quantity
     changes, and the drawer has no server-rendered bar at all. This script
     re-reads /cart.js after any cart mutation and rewrites every bar it finds,
     injecting one into the drawer footer if it is missing.

  2. Product page note. When a shopper picks a different variant Dawn re-renders
     the price, which can move the item across the free-delivery threshold. We
     watch the price element and re-word the note to match.

  The two numbers live in snippets/mdj-ship-rule.liquid and reach this file as
  data attributes — never hard-code them here.
*/
(function () {
  'use strict';

  var FALLBACK = { freeFrom: 400000, flatRate: 30000 };

  // Mirrors the store's money format exactly (Settings -> Store details ->
  // Currency formatting), which is currently "Rs.{amount}" with NO space. Liquid
  // renders the bar first and this script repaints it, so if the two disagree the
  // text visibly jumps. If the store's format is ever changed to "Rs. {amount}",
  // change the string below to match.
  function money(cents) {
    var whole = Math.round(cents / 100);
    return 'Rs.' + whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function readConfig(el) {
    var freeFrom = parseInt(el.getAttribute('data-free-from'), 10);
    var flatRate = parseInt(el.getAttribute('data-flat-rate'), 10);
    return {
      freeFrom: isNaN(freeFrom) ? FALLBACK.freeFrom : freeFrom,
      flatRate: isNaN(flatRate) ? FALLBACK.flatRate : flatRate
    };
  }

  /* ---------------- cart progress bar ---------------- */

  var TICK =
    '<span class="mdj-ship-bar__tick" aria-hidden="true">' +
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 7"/></svg>' +
    '</span>';

  function paintBar(bar, total) {
    var cfg = readConfig(bar);
    var unlocked = total >= cfg.freeFrom;
    var pct = cfg.freeFrom > 0 ? Math.min(100, (total * 100) / cfg.freeFrom) : 0;

    bar.classList.toggle('is-unlocked', unlocked);
    bar.setAttribute('data-total', String(total));

    var label = bar.querySelector('[data-mdj-ship-bar-label]');
    if (label) {
      label.innerHTML = unlocked
        ? TICK + 'Free delivery unlocked'
        : 'Add <strong>' + money(cfg.freeFrom - total) + '</strong> more for free delivery';
    }

    var fill = bar.querySelector('[data-mdj-ship-bar-fill]');
    if (fill) fill.style.width = pct.toFixed(1) + '%';

    var track = bar.querySelector('[data-mdj-ship-bar-track]');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));

    var rule = bar.querySelector('.mdj-ship-bar__rule');
    if (rule) {
      rule.textContent =
        money(cfg.flatRate) + ' delivery under ' + money(cfg.freeFrom) +
        ' · free at ' + money(cfg.freeFrom) + ' and above';
    }
  }

  function barTemplate() {
    // Cloned from a server-rendered bar so the drawer inherits the real numbers.
    var source = document.querySelector('[data-mdj-ship-bar]');
    if (!source) return null;
    var clone = source.cloneNode(true);
    clone.removeAttribute('id');
    return clone;
  }

  function ensureDrawerBar() {
    var drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (!drawer || drawer.querySelector('[data-mdj-ship-bar]')) return;
    // Empty drawers have nothing to progress towards.
    if (drawer.classList.contains('is-empty')) return;

    var anchor =
      drawer.querySelector('.cart-drawer__footer') ||
      drawer.querySelector('.drawer__footer') ||
      drawer.querySelector('.totals');
    if (!anchor) return;

    var bar = barTemplate();
    if (!bar) return;
    anchor.insertBefore(bar, anchor.firstChild);
  }

  var pending = null;
  function refresh() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          ensureDrawerBar();
          document.querySelectorAll('[data-mdj-ship-bar]').forEach(function (bar) {
            paintBar(bar, cart.total_price);
          });
        })
        .catch(function () { /* leave the server-rendered value alone */ });
    }, 120);
  }

  // Dawn fires this on add-to-cart; other themes and apps use it too.
  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart:update', refresh);
  document.addEventListener('cart-drawer:open', refresh);

  // Every cart mutation in Dawn goes through one of these endpoints, so
  // wrapping fetch is enough to catch quantity changes, adds and removals.
  //
  // Deliberately NOT a MutationObserver on the cart containers: this script
  // writes into those same containers (it repaints the bar and injects one
  // into the drawer), so an observer would re-trigger itself on its own edits
  // and loop forever, re-fetching /cart.js on every pass.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        result.then(function () { refresh(); }).catch(function () {});
      }
      return result;
    };
  }

  // XHR too — some apps still use it to write to the cart.
  if (window.XMLHttpRequest && XMLHttpRequest.prototype.open) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
        this.addEventListener('load', function () { refresh(); });
      }
      return nativeOpen.apply(this, arguments);
    };
  }

  /* ---------------- product page note ---------------- */

  function priceFromText(text) {
    var digits = (text || '').replace(/[^0-9.]/g, '');
    if (!digits) return null;
    var value = parseFloat(digits);
    return isNaN(value) ? null : Math.round(value * 100);
  }

  function paintNote(note, cents) {
    // The note carries the rule on its own data attributes, so it stays correct
    // on a product page with an empty cart, where no cart bar exists to read.
    var source = note.hasAttribute('data-free-from')
      ? note
      : document.querySelector('[data-mdj-ship-bar]');
    var cfg = source ? readConfig(source) : FALLBACK;
    var lead = note.querySelector('[data-mdj-ship-note-lead]');
    var rest = note.querySelector('[data-mdj-ship-note-rest]');
    if (!lead || !rest || cents === null) return;

    if (cents >= cfg.freeFrom) {
      lead.textContent = 'Free delivery on this item';
      rest.innerHTML = ' · orders of ' + money(cfg.freeFrom) + ' and above ship free';
    } else {
      lead.textContent = money(cfg.flatRate) + ' delivery';
      rest.innerHTML = ' · free once your order reaches ' + money(cfg.freeFrom);
    }
  }

  // Watches the price for variant changes. Scoped to the price element only,
  // and the note lives outside it, so repainting the note cannot re-trigger
  // this observer.
  function watchNote() {
    var note = document.querySelector('[data-mdj-ship-note]');
    if (!note || !('MutationObserver' in window)) return false;

    var priceBox = document.querySelector('.product__info-container .price');
    if (!priceBox) return false;

    var mo = new MutationObserver(function () {
      var live = priceBox.querySelector('.price-item--sale, .price-item--regular');
      if (live) paintNote(note, priceFromText(live.textContent));
    });
    mo.observe(priceBox, { childList: true, subtree: true, characterData: true });
    return true;
  }

  var noteWatched = false;
  function boot() {
    if (!noteWatched && watchNote()) noteWatched = true;
    ensureDrawerBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
